import React, { useEffect, useMemo, useState } from 'react';
import { FiEdit2, FiTrash2 } from 'react-icons/fi';
import { Button, Input, Select, Table } from 'components/common';
import { deleteLanguage, getAdminLanguages as getLanguages } from 'apis/adminLanguages';
import { ConfirmationDialog, useToast } from 'components/admin';
import { useAdminDeleteMutation, useAdminListQuery } from 'hooks';
import { useNavigate } from 'react-router-dom';
import styles from './Languages.module.scss';
import '../../../styles/admin-shared.scss';

const LanguagesList = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(10);
    const [keyword, setKeyword] = useState('');
    const [isActiveFilter, setIsActiveFilter] = useState('');
    const [deleteId, setDeleteId] = useState(null);
    const toast = useToast();
    const navigate = useNavigate();

    const queryParams = useMemo(() => ({
        pageNumber: currentPage,
        pageSize,
        keyword: keyword || undefined,
        isActive: isActiveFilter === '' ? undefined : isActiveFilter === 'true',
    }), [currentPage, pageSize, keyword, isActiveFilter]);

    useEffect(() => {
        setCurrentPage(1);
    }, [keyword, isActiveFilter]);

    const { data = { items: [], total: 0, currentPage, pageSize }, isFetching, error } = useAdminListQuery('languages', queryParams, getLanguages);
    const deleteMutation = useAdminDeleteMutation('languages', deleteLanguage);

    useEffect(() => {
        if (error) toast.error(error.message);
    }, [error, toast]);

    const handleDelete = async () => {
        const res = await deleteMutation.mutateAsync(deleteId);
        if (res.success) {
            toast.success('Xóa thành công');
        } else {
            toast.error(res.message);
        }
        setDeleteId(null);
    };

    const columns = [
        { field: 'name', header: 'Tên' },
        { field: 'code', header: 'Mã' },
        { field: 'isActive', header: 'Trạng thái', render: (row) => <span className={`status-badge ${row.isActive ? 'active' : 'inactive'}`}>{row.isActive ? 'Hoạt động' : 'Không hoạt động'}</span> },
        { field: 'sortOrder', header: 'Thứ tự' },
        {
            header: 'Thao tác',
            render: (row) => (
                <div className="action-buttons">
                    <button className="icon-btn icon-btn--edit" onClick={() => navigate(`/admin/languages/edit/${row.id}`)} title="Sửa"><FiEdit2 size={15} /></button>
                    <button className="icon-btn icon-btn--delete" onClick={() => setDeleteId(row.id)} title="Xóa"><FiTrash2 size={15} /></button>
                </div>
            ),
        },
    ];

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h1>Ngôn ngữ</h1>
                <Button onClick={() => navigate('/admin/languages/create')}>Thêm Mới</Button>
            </div>
            <div className={styles.filters}>
                <Input placeholder="Tìm kiếm theo tên/mã" value={keyword} onChange={(e) => setKeyword(e.target.value)} />
                <Select
                    value={isActiveFilter}
                    onChange={(e) => setIsActiveFilter(e.target.value)}
                    options={[
                        { value: '', label: 'Tất cả' },
                        { value: 'true', label: 'Hoạt động' },
                        { value: 'false', label: 'Không hoạt động' },
                    ]}
                />
            </div>
            <Table
                columns={columns}
                data={data.items}
                loading={isFetching}
                pagination={{
                    currentPage: data.currentPage,
                    totalPages: Math.ceil(data.total / data.pageSize),
                    onPageChange: setCurrentPage,
                }}
            />
            <ConfirmationDialog
                open={!!deleteId}
                onClose={() => setDeleteId(null)}
                onConfirm={handleDelete}
                title="Xác nhận Xóa"
                message="Bạn có chắc chắn muốn xóa?"
            />
        </div>
    );
};

export default LanguagesList;
